/**
 * Timeframe preset options for filtering data by date.
 * Common presets used across filters (transactions, campaigns, etc.)
 */
export type TTimeframePreset =
  | 'today'
  | 'last_7_days'
  | 'last_30_days'
  | 'last_90_days'
  | 'all_time'
  | 'custom';

/**
 * Timeframe preset metadata for building select options.
 * Labels should be translated by the consumer using t(option.labelKey).
 */
export const TIMEFRAME_PRESETS: Array<{ id: TTimeframePreset; labelKey: string }> = [
  { id: 'all_time', labelKey: 'All time' },
  { id: 'today', labelKey: 'Today' },
  { id: 'last_7_days', labelKey: 'Last 7 days' },
  { id: 'last_30_days', labelKey: 'Last 30 days' },
  { id: 'last_90_days', labelKey: 'Last 90 days' },
  { id: 'custom', labelKey: 'Custom range' },
];

interface IDateRange {
  start: Date | null;
  end: Date | null;
}

const DEFAULT_CUSTOM_DATE_RANGE: IDateRange = { start: null, end: null };

/**
 * Calculate date range from a timeframe preset or custom range.
 * Returns UTC dates suitable for API filtering.
 *
 * @param timeframe - The timeframe preset
 * @param customDateRange - Custom date range (used when timeframe === 'custom')
 * @returns Start and end dates, or null values for no date filtering
 *
 * @example
 * ```ts
 * // Get last 7 days range
 * const range = getDateRangeFromPreset('last_7_days');
 * // { start: Date(7 days ago at 00:00:00), end: Date(today at 23:59:59) }
 *
 * // No date filtering
 * const allTime = getDateRangeFromPreset('all_time');
 * // { start: null, end: null }
 *
 * // Custom range
 * const custom = getDateRangeFromPreset('custom', { start: new Date('2024-01-01'), end: new Date('2024-01-31') });
 * // { start: Date(2024-01-01), end: Date(2024-01-31) }
 * ```
 */
export function getDateRangeFromPreset(
  timeframe: TTimeframePreset,
  customDateRange: IDateRange = DEFAULT_CUSTOM_DATE_RANGE,
): IDateRange {
  const now = new Date();

  const endOfToday = new Date(now);
  endOfToday.setUTCHours(23, 59, 59, 999);
  endOfToday.setUTCFullYear(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

  const startOfToday = new Date(now);
  startOfToday.setUTCHours(0, 0, 0, 0);
  startOfToday.setUTCFullYear(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

  switch (timeframe) {
    case 'all_time':
      return { start: null, end: null };

    case 'today':
      return { start: startOfToday, end: endOfToday };

    case 'last_7_days': {
      const start = new Date(startOfToday);
      start.setUTCDate(start.getUTCDate() - 7);
      return { start, end: endOfToday };
    }

    case 'last_30_days': {
      const start = new Date(startOfToday);
      start.setUTCDate(start.getUTCDate() - 30);
      return { start, end: endOfToday };
    }

    case 'last_90_days': {
      const start = new Date(startOfToday);
      start.setUTCDate(start.getUTCDate() - 90);
      return { start, end: endOfToday };
    }

    case 'custom':
      return {
        start: customDateRange.start,
        end: customDateRange.end,
      };

    default:
      return { start: null, end: null };
  }
}
